import type { FilterConfigItem } from "@shared/components/DynamicFilter/index.vue";
import type { PaginatedQuery } from "@shared/types/list";
import { DEFAULT_PAGE_SIZE } from "@shared/types/list";
import { assignCombinedSearchParam, resolveMultiSelectForRequest, type CombinedSearchValue } from "@shared/utils/filterQuery";
import { t } from "@shared/utils/i18n";
import { DF_SELLABLE_STATUS_OPTIONS, type DfZoneStockBucket, type DfZoneStockQuickFilter } from "./data";

export type { DfZoneStockBucket, DfZoneStockQuickFilter };

export interface DfSellableQueryParam extends PaginatedQuery {
    /** 店铺（侧栏一级，来自 storeList） */
    storeId: string;
    /** 与 storeId 同步的 VC Code（请求体 vcCode） */
    vcCode: string;
    /** 企业 ID（下拉 GET /manage/corp/options） */
    corpIds: string[];
    /** 状态多选：1 正常同步 / 0 暂停同步 → syncStatuses */
    syncStatuses: number[];
    /**
     * 1P 仓库（与产品配对一致）
     * 下拉 POST /manage/warehouse/external/options，value=externalWarehouseId
     */
    externalWarehouseId: string[];
    /** 仓库代码（下拉 POST /manage/warehouseAmazonVc/options vcType=DF） */
    warehouseCode: string[];
    /** 组合搜索当前字段键（由 DynamicFilter / UiCombinedSelect 维护） */
    df_search?: string;
    asin: CombinedSearchValue;
    sku1p: CombinedSearchValue;
    vcSku: CombinedSearchValue;
    /** 快捷筛选：分区库存 bucket（侧栏二级多选 → zoneStockBuckets） */
    zoneStockBuckets: string[];
    /** 过滤未关联1P仓库（操作栏勾选，默认 true → onlyBoundWarehouse=1） */
    filterUnlinkedWarehouse: boolean;
    /** 过滤1P仓库0库存（操作栏勾选，默认 true → onlyHasStockWarehouse=1） */
    filterZeroStockWarehouse: boolean;
}

/** POST /manage/amazonVcDfSellableStock/list 请求体 */
export interface DfSellableListRequestParams {
    page: number;
    perPage: number;
    storeId?: string;
    vcCode?: string;
    corpIds?: string[];
    warehouseCodes?: (number | string)[];
    syncStatuses?: number[];
    externalWarehouseIds?: string[];
    ownerUserIds?: string[];
    warehouseSku?: string;
    warehouseSkus?: string[];
    asin?: string;
    asins?: string[];
    sku1p?: string;
    sku1ps?: string[];
    vcSku?: string;
    vcSkus?: string[];
    itemName?: string;
    productName?: string;
    /** 分区库存快捷筛选（多选） */
    zoneStockBuckets?: string[];
    /** 1 仅关联第三方仓库；不传 / 0 不筛选 */
    onlyBoundWarehouse?: 0 | 1;
    /** 1 仅有库存仓库；不传 / 0 不筛选 */
    onlyHasStockWarehouse?: 0 | 1;
}

export const DEFAULT_DF_SELLABLE_QUERY: DfSellableQueryParam = {
    storeId: "",
    vcCode: "",
    corpIds: [],
    syncStatuses: [],
    externalWarehouseId: [],
    warehouseCode: [],
    asin: "",
    sku1p: "",
    vcSku: "",
    zoneStockBuckets: [],
    filterUnlinkedWarehouse: true,
    filterZeroStockWarehouse: true,
    page: 1,
    limit: DEFAULT_PAGE_SIZE,
};

/** UI query → 列表 POST body */
export function toDfSellableListRequestParams(query: DfSellableQueryParam): DfSellableListRequestParams {
    const params: DfSellableListRequestParams = {
        page: query.page,
        perPage: query.limit,
        storeId: query.storeId?.trim() || "0",
        vcCode: query.vcCode?.trim() || "",
    };

    const corpIds = resolveMultiSelectForRequest(query.corpIds);
    if (corpIds !== undefined) params.corpIds = corpIds.map(String);

    const warehouseCodes = resolveMultiSelectForRequest(query.warehouseCode);
    if (warehouseCodes !== undefined) params.warehouseCodes = warehouseCodes;

    const externalWarehouseIds = resolveMultiSelectForRequest(query.externalWarehouseId);
    if (externalWarehouseIds !== undefined) params.externalWarehouseIds = externalWarehouseIds.map(String);

    const zoneStockBuckets = resolveMultiSelectForRequest(query.zoneStockBuckets);
    if (zoneStockBuckets !== undefined) params.zoneStockBuckets = zoneStockBuckets.map(String);

    const syncStatuses = resolveMultiSelectForRequest(query.syncStatuses);
    if (syncStatuses !== undefined) {
        params.syncStatuses = syncStatuses.map(v => Number(v)).filter(v => Number.isFinite(v));
    }

    assignCombinedSearchParam(params, query.asin, "asin", "asins");
    assignCombinedSearchParam(params, query.sku1p, "sku1p", "sku1ps");
    assignCombinedSearchParam(params, query.vcSku, "vcSku", "vcSkus");

    if (query.filterUnlinkedWarehouse) {
        params.onlyBoundWarehouse = 1;
    }
    if (query.filterZeroStockWarehouse) {
        params.onlyHasStockWarehouse = 1;
    }

    return params;
}

export const DF_COMBINED_SEARCH_OPTIONS = [
    { label: "ASIN", value: "asin" },
    { label: t("1PSKU"), value: "sku1p" },
    { label: "VC SKU", value: "vcSku" },
];

/** 店铺 Tab / 快捷筛选已迁至 UiStatusSidebar */
export function buildDfSellableFilterConfig(): FilterConfigItem[] {
    return [
        {
            type: "UiMultipleSelect",
            key: "corpIds",
            label: t("企业名称"),
            placeholder: t("企业名称"),
            options: "corpOptions",
            width: 120,
            props: { filterable: true },
        },
        {
            type: "UiMultipleSelect",
            key: "syncStatuses",
            label: t("状态"),
            placeholder: t("状态"),
            options: DF_SELLABLE_STATUS_OPTIONS,
            width: 100,
        },
        {
            type: "UiMultipleSelect",
            key: "externalWarehouseId",
            label: t("1P仓库名称"),
            placeholder: t("1P仓库名称"),
            options: "warehouseNameOptions",
            width: 140,
            props: { filterable: true },
        },
        {
            type: "UiMultipleSelect",
            key: "warehouseCode",
            label: t("仓库代码"),
            placeholder: t("仓库代码"),
            options: "warehouseCodeOptions",
            width: 110,
            props: { filterable: true },
        },
        {
            type: "UiCombinedSelect",
            key: "df_search",
            label: t("搜索"),
            options: DF_COMBINED_SEARCH_OPTIONS,
            props: { selectValue: "asin", placeholder: t("搜索内容") },
        },
    ];
}
